// React
import React from 'react';
import PropTypes from 'prop-types';
import shortid from 'shortid';

// CYBS-UI
import { StructFlex } from '@cybs-ui/components/lib/struct-flex';

// Components
import RightSide from './components/right';
import LeftSide from './components/left';

// Styles
import stylesBuilder from './component.styles';

const ActionBar = ({
  dataTest,
  gridTableProps,
  hasTopBorder,
  headingLevel,
  isActionBarActive,
  isInvalid,
  isMoreActionsOpen,
  normalTitleSize,
  numberOfActionsToShow,
  leftActions,
  rightActions,
  statusBadge,
  collapsible,
  i18n
}) => {
  const sectionProps = {
    actionBarId: shortid.generate(),
    dataTest,
    headingLevel,
    gridTableProps,
    isActionBarActive,
    isInvalid,
    isMoreActionsOpen,
    normalTitleSize,
    numberOfActionsToShow,
    leftActions,
    rightActions,
    statusBadge,
    i18n,
    collapsible
  };

  const { headerStyle, titleStyle, buttonStyle } = stylesBuilder({
    isInvalid,
    isActionBarActive,
    hasTopBorder,
    collapsible
  });

  return (
    <StructFlex
      align-items="center"
      css={headerStyle}
      direction="row"
      justify-content="space-between"
      shrink="0"
      flex-wrap="wrap"
      data-test={`${dataTest}-cybsActionBar`}
    >
      <StructFlex align-items="center" css={titleStyle} flex-wrap="wrap">
        <LeftSide {...sectionProps} />
      </StructFlex>
      <StructFlex align-items="center" flex-wrap="wrap" css={buttonStyle}>
        <RightSide {...sectionProps} />
      </StructFlex>
    </StructFlex>
  );
};

ActionBar.propTypes = {
  /**
   * Level of the heading displayed before the component
   */
  headingLevel: PropTypes.number,
  /**
   * Props from gridtable passed to generate strings.
   */
  gridTableProps: PropTypes.oneOfType([PropTypes.any, PropTypes.bool]),
  /**
   * Determines whether to show top border
   */
  hasTopBorder: PropTypes.bool,
  /**
   * On Change is used for advanced dropdown menu for overflow Actions
   * */
  onChange: PropTypes.func,
  /**
   * Number of buttons to show before overflowing into menu.
   * */
  numberOfActionsToShow: PropTypes.number,
  /**
   *  Actions on the left side of the bar. May pass array of button configs
   *  or a react element.
   */
  leftActions: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(
      PropTypes.shape({
        active: PropTypes.bool,
        ariaLabel: PropTypes.string,
        colorScheme: PropTypes.string,
        disabled: PropTypes.func,
        iconClass: PropTypes.string,
        iconLocation: PropTypes.string,
        isFullWidth: PropTypes.func,
        href: PropTypes.string,
        label: PropTypes.string,
        onClick: PropTypes.func,
        onKeyDown: PropTypes.func,
        tag: PropTypes.string,
        variant: PropTypes.string
      })
    )
  ]),
  /**
   * Actions on the right side of the bar. May pass array of button configs
   * or a react element.
   */

  rightActions: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(
      PropTypes.shape({
        ariaLabel: PropTypes.string,
        colorScheme: PropTypes.string,
        disabled: PropTypes.func,
        iconClass: PropTypes.string,
        iconLocation: PropTypes.string,
        href: PropTypes.string,
        label: PropTypes.string,
        onClick: PropTypes.func,
        onKeyDown: PropTypes.func,
        tag: PropTypes.string,
        variant: PropTypes.string
      })
    )
  ]),
  /**
   * Invalid entry or selection specifically for use with gridtable.
   */
  isInvalid: PropTypes.bool,

  /**
   * Required data testing string
   */
  dataTest: PropTypes.string.isRequired,

  /**
   * Action Bar for Grid proptypes
   */
  isActionBarActive: PropTypes.bool,

  /**
   * Is the overflow menu open.
   */
  isMoreActionsOpen: PropTypes.bool,

  /**
   * Strings for the component.
   */
  i18n: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      title: PropTypes.string,
      subtitle: PropTypes.string,
      additionalSubtitle: PropTypes.string,
      collapsibleButtonLabel: PropTypes.string,
      collapsedLabel: PropTypes.string,
      expandedLabel: PropTypes.string,
      moreActionsLabel: PropTypes.string
    })
  ]),

  /**
   * Collapsible configuration
   */
  collapsible: PropTypes.shape({
    isOpen: PropTypes.bool,
    onClick: PropTypes.func,
    location: PropTypes.oneOf(['left', 'right'])
  }),

  /**
   * Status badge for the action bar.
   */
  statusBadge: PropTypes.shape({
    dataTest: PropTypes.string,
    status: PropTypes.string,
    icon: PropTypes.string,
    label: PropTypes.string,
    textOnly: PropTypes.bool
  }),

  /**
   * Title size to be normal instead of displayed header level
   */
  normalTitleSize: PropTypes.bool
};

ActionBar.defaultProps = {
  collapsible: undefined,
  gridTableProps: undefined,
  hasTopBorder: false,
  headingLevel: 3,
  i18n: {
    title: 'Search Results: ',
    subtitle: 'items selected',
    additionalSubtitle: 'column filters currently applied',
    collapsibleButtonLabel: 'Accordion toggle',
    moreActionsLabel: 'More Actions'
  },
  isActionBarActive: undefined,
  isInvalid: false,
  isMoreActionsOpen: false,
  leftActions: [],
  normalTitleSize: false,
  numberOfActionsToShow: Infinity,
  statusBadge: undefined,
  onChange: () => {}, // eslint-disable-line
  rightActions: []
};

ActionBar.displayName = 'ActionBar';
export default ActionBar;
